import { useI18n } from '@ecom/i18n';
import { forwardRef, type SelectHTMLAttributes } from 'react';

import { cx } from '../utils/cx';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
}

/**
 * Native `<select>` rather than a custom listbox.
 *
 * A native select gets keyboard handling, screen-reader announcement, and
 * correct RTL mirroring of its dropdown arrow for free, in every browser —
 * properties a hand-built listbox has to re-implement and re-test. For the
 * form controls this design system needs (governorate, delegation, sort
 * order), that trade is clearly worth it.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, ...rest },
  ref,
) {
  const { t } = useI18n();
  const resolvedPlaceholder = placeholder ?? t('common.selectPlaceholder');

  return (
    <select ref={ref} className={cx('ecom-select', className)} {...rest}>
      {rest.required !== true ? <option value="">{resolvedPlaceholder}</option> : null}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
});
